import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Category as VectorIcon,
  Face as FaceIcon,
} from '@mui/icons-material';

const FacetedSearch = ({ filters, onFiltersChange, resultCount = 0 }) => {
  const [expandedPanels, setExpandedPanels] = useState(['fileType', 'peopleCount']);

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev =>
      isExpanded
        ? [...prev, panel]
        : prev.filter(p => p !== panel)
    );
  };

  const handleFilterChange = (filterType, value) => {
    const currentFilters = filters[filterType] || [];
    const newFilters = currentFilters.includes(value)
      ? currentFilters.filter(f => f !== value)
      : [...currentFilters, value];

    onFiltersChange({
      ...filters,
      [filterType]: newFilters
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((count, filterArray) => count + filterArray.length, 0);
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'image':
        return <ImageIcon sx={{ fontSize: 16, mr: 1 }} />;
      case 'vector':
        return <VectorIcon sx={{ fontSize: 16, mr: 1 }} />;
      case 'video':
        return <VideoIcon sx={{ fontSize: 16, mr: 1 }} />;
      default:
        return null;
    }
  };

  return (
    <Paper
      sx={{
        width: 260,
        height: 'fit-content',
        backgroundColor: '#161616',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 1,
        position: 'fixed',
        left: 16,
        top: 100,
        zIndex: 1000,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1.1rem' }}>
            Refine
          </Typography>
          {getActiveFilterCount() > 0 && (
            <Chip
              label={`${getActiveFilterCount()} filter${getActiveFilterCount() > 1 ? 's' : ''}`}
              size="small"
              onDelete={clearAllFilters}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 1)',
                  }
                }
              }}
            />
          )}
        </Box>

        {resultCount > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.875rem'
            }}
          >
            {resultCount.toLocaleString()} results
          </Typography>
        )}
      </Box>

      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }} />

      {/* File Type Filter */}
      <Accordion
        expanded={expandedPanels.includes('fileType')}
        onChange={handlePanelChange('fileType')}
        sx={{
          background: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': {
            px: 2.5,
            py: 1.5,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto',
            }
          },
          '& .MuiAccordionDetails-root': {
            px: 2.5,
            py: 1,
            pt: 0,
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontWeight: 500,
              fontSize: '1rem'
            }}
          >
            File Type
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {[
                { value: 'image', label: 'Image', count: resultCount }, // For now, all results are images
                { value: 'vector', label: 'Vector', count: 0 },
                { value: 'video', label: 'Video', count: 0 }
              ].map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={filters.fileType?.includes(option.value) || false}
                      onChange={() => handleFilterChange('fileType', option.value)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&.Mui-checked': {
                          color: '#ffffff',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 18,
                        },
                      }}
                      disabled={option.count === 0}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getFileTypeIcon(option.value)}
                        <Typography
                          sx={{
                            color: option.count === 0 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.875rem'
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.75rem',
                          ml: 1
                        }}
                      >
                        {option.count > 0 ? option.count.toLocaleString() : '0'}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    mx: 0,
                    '& .MuiFormControlLabel-label': {
                      width: '100%',
                    },
                    '&:hover': {
                      backgroundColor: option.count > 0 ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                    },
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Stock Source Filter */}
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', mx: 2 }} />

      <Accordion
        expanded={expandedPanels.includes('stockSource')}
        onChange={handlePanelChange('stockSource')}
        sx={{
          background: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': {
            px: 2.5,
            py: 1.5,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto',
            }
          },
          '& .MuiAccordionDetails-root': {
            px: 2.5,
            py: 1,
            pt: 0,
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontWeight: 500,
              fontSize: '1rem'
            }}
          >
            Stock Source
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {[
                { value: 'istock', label: 'iStock' },
                { value: 'shutterstock', label: 'Shutterstock' },
                { value: 'adobe', label: 'Adobe Stock' },
                { value: 'other', label: 'Other' }
              ].map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={filters.stockSource?.includes(option.value) || false}
                      onChange={() => handleFilterChange('stockSource', option.value)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&.Mui-checked': {
                          color: '#ffffff',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 18,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.875rem'
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={{
                    mx: 0,
                    '& .MuiFormControlLabel-label': {
                      width: '100%',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* People Count Filter */}
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', mx: 2 }} />

      <Accordion
        expanded={expandedPanels.includes('peopleCount')}
        onChange={handlePanelChange('peopleCount')}
        sx={{
          background: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': {
            px: 2.5,
            py: 1.5,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto',
            }
          },
          '& .MuiAccordionDetails-root': {
            px: 2.5,
            py: 1,
            pt: 0,
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontWeight: 500,
              fontSize: '1rem'
            }}
          >
            Number of People
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {[
                { value: 'none', label: 'No people' },
                { value: 'one', label: 'One person' },
                { value: 'two', label: 'Two people' },
                { value: '3', label: '3 people' },
                { value: '4', label: '4 people' },
                { value: '5', label: '5 people' },
                { value: '5+', label: '5+ people' }
              ].map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={filters.peopleCount?.includes(option.value) || false}
                      onChange={() => handleFilterChange('peopleCount', option.value)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&.Mui-checked': {
                          color: '#ffffff',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 18,
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FaceIcon sx={{ fontSize: 16, mr: 1, color: 'rgba(255, 255, 255, 0.6)' }} />
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.875rem'
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    mx: 0,
                    '& .MuiFormControlLabel-label': {
                      width: '100%',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default FacetedSearch;